import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Trash2, Mail, Filter, CheckSquare, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function MessagesAdmin() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get("/admin/messages"),
        api.get("/admin/contact-request-types/").catch(() => ({ data: [] })),
      ]);
      setItems(m.data); setTypes(t.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (typeFilter === "all") return items;
    if (typeFilter === "__none__") return items.filter((m) => !m.request_type);
    return items.filter((m) => m.request_type === typeFilter);
  }, [items, typeFilter]);

  const toggleOne = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const allSelected = filtered.length > 0 && filtered.every((m) => selected.has(m.id));
  const toggleAll = () => {
    const s = new Set(selected);
    if (allSelected) filtered.forEach((m) => s.delete(m.id));
    else filtered.forEach((m) => s.add(m.id));
    setSelected(s);
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete message?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} message${selected.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setDeleting(true);
    let failed = 0;
    await Promise.all([...selected].map(async (id) => {
      try { await api.delete(`/admin/messages/${id}`); }
      catch { failed++; }
    }));
    setItems((p) => p.filter((i) => !selected.has(i.id)));
    setSelected(new Set());
    setDeleting(false);
    toast.success(`Deleted ${selected.size - failed} message${selected.size - failed === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
  };

  // Count messages per request type for filter dropdown
  const counts = useMemo(() => {
    const c = { all: items.length, __none__: 0 };
    items.forEach((m) => {
      if (!m.request_type) c.__none__++;
      else c[m.request_type] = (c[m.request_type] || 0) + 1;
    });
    return c;
  }, [items]);

  return (
    <div data-testid="admin-messages-page">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Inbox</div>
          <h1 className="font-serif text-4xl mt-2 text-ink">Contact messages</h1>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-white border border-sand rounded-2xl p-3">
        <button
          type="button" onClick={toggleAll} data-testid="msg-toggle-all"
          className="inline-flex items-center gap-2 text-sm text-ink/75 hover:text-navy px-3 py-1.5 rounded-lg hover:bg-sand/40"
          disabled={filtered.length === 0}
        >
          {allSelected ? <CheckSquare size={14} /> : <Square size={14} />}
          {allSelected ? "Unselect all" : "Select all"}
        </button>

        <div className="inline-flex items-center gap-2 text-sm">
          <Filter size={14} className="text-ink/50" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="msg-filter-type"
            className="px-3 py-1.5 rounded-lg border border-sand focus:outline-none focus:border-navy bg-white text-sm"
          >
            <option value="all">All types ({counts.all})</option>
            {counts.__none__ > 0 && <option value="__none__">— No type ({counts.__none__})</option>}
            {types.map((t) => (
              <option key={t.id} value={t.name}>{t.name} ({counts[t.name] || 0})</option>
            ))}
          </select>
        </div>

        <div className="flex-1" />

        {selected.size > 0 && (
          <button
            type="button" onClick={deleteSelected} disabled={deleting}
            data-testid="msg-delete-selected"
            className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-60"
          >
            {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Delete {selected.size} selected
          </button>
        )}
      </div>

      {loading ? <div className="text-ink/40">Loading…</div> :
        filtered.length === 0 ? (
          <div className="rounded-3xl bg-white border border-sand p-14 text-center text-ink/60">
            {items.length === 0 ? "No messages." : "No messages match this filter."}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((m) => {
              const isSel = selected.has(m.id);
              return (
                <div key={m.id} data-testid={`msg-row-${m.id}`} className={`rounded-2xl bg-white border overflow-hidden transition-colors ${isSel ? "border-navy bg-navy/[0.02]" : "border-sand"}`}>
                  <div className="flex items-center">
                    <label className="px-4 py-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleOne(m.id)}
                        data-testid={`msg-check-${m.id}`}
                        className="w-4 h-4 accent-navy"
                      />
                    </label>
                    <button onClick={() => setOpen(open === m.id ? null : m.id)} className="flex-1 text-left px-2 py-4 flex justify-between items-center hover:bg-cream/60">
                      <div className="min-w-0">
                        <div className="font-medium text-ink">{m.name} <span className="text-ink/50 text-sm">· {m.email}</span></div>
                        <div className="text-sm text-ink/60 truncate max-w-2xl">
                          {m.request_type && <span className="inline-block mr-2 text-[9px] uppercase tracking-wider bg-gold/20 text-navy px-2 py-0.5 rounded-full">{m.request_type}</span>}
                          {m.subject || m.message.slice(0, 80)}
                        </div>
                      </div>
                      <div className="text-xs text-ink/50 shrink-0 pl-4">{new Date(m.created_at).toLocaleDateString()}</div>
                    </button>
                  </div>
                  {open === m.id && (
                    <div className="px-5 pb-5 text-ink/75 text-sm whitespace-pre-wrap border-t border-sand pt-4">
                      <div className="mb-3 flex gap-4 items-center text-xs text-ink/60 flex-wrap">
                        {m.request_type && <span className="text-[9px] uppercase tracking-wider bg-gold/20 text-navy px-2 py-0.5 rounded-full">{m.request_type}</span>}
                        <span>Phone: {m.phone || "—"}</span>
                        <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1 text-emerald_deep"><Mail size={12} /> Reply</a>
                      </div>
                      <div className="mb-4"><strong>{m.subject || "(no subject)"}</strong></div>
                      {m.message}
                      <div className="mt-4">
                        <button onClick={() => deleteOne(m.id)} className="text-red-600 hover:text-red-800 text-xs inline-flex items-center gap-1.5"><Trash2 size={12} /> Delete</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
